import './EditGroup.sass';

import React, { FC, useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { groupsActions } from '$store/groups';
import { Button, CellButton, Form, FormItem, Header, Input, ScrollView } from '$uikit';
import { BottomBar } from '$uikit';
import {
  MAX_GROUP_DESCRIPTION,
  MAX_GROUP_LINK,
  MAX_GROUP_NAME,
} from '$shared/constants/groups';
import { useGroupInfo, useTranslator } from '$hooks';
import { EditGroupProps } from './EditGroup.interface';
import { IcStar28 } from '$assets';

export const EditGroup: FC<EditGroupProps> = ({ groupId }) => {
  const group = useGroupInfo(groupId);
  const dispatch = useDispatch();
  const [name, setName] = useState(group?.name ?? '');
  const [description, setDescription] = useState(group?.description ?? '');
  const [link, setLink] = useState(group?.link ?? '');
  const t = useTranslator();

  const handleName = useCallback(
    ({ target }) => {
      setName(target.value.substr(0, MAX_GROUP_NAME));
    },
    [setName],
  );

  const handleDescription = useCallback(
    ({ target }) => {
      setDescription(target.value.substr(0, MAX_GROUP_DESCRIPTION));
    },
    [setDescription],
  );

  const handleLink = useCallback(
    ({ target }) => {
      setLink(target.value.substr(0, MAX_GROUP_LINK));
    },
    [setLink],
  );

  const nameLabel = useMemo(() => {
    const avail = MAX_GROUP_NAME - name.length;
    return t('symbols_limit', { count: avail });
  }, [name, t]);

  const descriptionLabel = useMemo(() => {
    const avail = MAX_GROUP_DESCRIPTION - description.length;
    return t('symbols_limit', { count: avail });
  }, [description, t]);

  const handleSave = useCallback(() => {
    dispatch(
      groupsActions.saveGroup({ groupId, name, description, link, isAdultOnly: false }),
    );
  }, [groupId, name, description, link, dispatch]);

  return (
    <>
      <Header showCloseButton>{t('edit_group_title')}</Header>
      <ScrollView>
        <Form>
          <FormItem title={t('edit_group_name')} description={nameLabel}>
            <Input
              placeholder={t('edit_group_name_placeholder')}
              value={name}
              onChange={handleName}
              maxLength={MAX_GROUP_NAME}
            />
          </FormItem>
          <FormItem title={t('edit_group_description')} description={descriptionLabel}>
            <Input
              placeholder={t('edit_group_description_placeholder')}
              multiline
              value={description}
              onChange={handleDescription}
              maxLength={MAX_GROUP_DESCRIPTION}
            />
          </FormItem>
          <FormItem title={t('edit_group_link')} description={t('edit_group_link_tip')}>
            <Input
              placeholder={t('edit_group_link_placeholder')}
              value={link}
              onChange={handleLink}
              maxLength={MAX_GROUP_LINK}
            />
          </FormItem>
        </Form>
        <CellButton icon={<IcStar28 />} modal={`group_admins${groupId}`}>
          {t('edit_group_admins')}
        </CellButton>
      </ScrollView>
      <BottomBar fixed>
        <Button stretched onClick={handleSave}>
          {t('save')}
        </Button>
      </BottomBar>
    </>
  );
};
